import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import {
  DEFAULT_CURRENCY,
  RUB_STEP,
  RUB_STEP_PER_TH,
  USD_STEP,
  USD_STEP_PER_TH
} from '@/lib/constants'
import { DeviceSortType, SortOrder } from '@/lib/types'

import useDevicesForSale from '@/hooks/marketplace/useDevicesForSale'
import {
  DeviceFilterStore,
  useDeviceFilterStore
} from '@/hooks/stores/useDeviceFilterStore'
import useUserInfo from '@/hooks/users/useUserInfo'

interface MinersSort {
  order: SortOrder
  sort: DeviceSortType
}

export const useShopPage = () => {
  const router = useRouter()
  const { formatMessage } = useIntl()
  const { currency: userCurrency } = useUserInfo()
  const { order, sort, totalPrice, pricePerTH, reorder } =
    useDeviceFilterStore()
  const { data, isFetching, isFetchingNextPage, fetchNextPage } =
    useDevicesForSale()
  const [paymentFailed, setPaymentFailed] = useState(false)
  const [maxPrice, setMaxPrice] = useState<number>()
  const [maxPricePerTH, setMaxPricePerTH] = useState<number>()
  const [minPrice, setMinPrice] = useState<number>()
  const [minPricePerTH, setMinPricePerTH] = useState<number>()
  const [areSamePrices, setAreSamePrices] = useState(false)

  const currency = userCurrency || DEFAULT_CURRENCY
  const step = currency === 'USD' ? USD_STEP : RUB_STEP
  const stepPerTH = currency === 'USD' ? USD_STEP_PER_TH : RUB_STEP_PER_TH

  useEffect(() => {
    if (router.query.payment_status === 'failure') {
      setPaymentFailed(true)
    }
  }, [router.query.payment_status])

  const SORT_OPTIONS = [
    {
      id: 'shop.filter.sort.asc',
      title: 'Цена по убыванию',
      value: { sort: 'price', order: 'asc' }
    },
    {
      id: 'shop.filter.sort.desc',
      title: 'Цена по возрастанию',
      value: { sort: 'price', order: 'desc' }
    },
    {
      id: 'shop.filter.sort.roi',
      title: 'По окупаемости (ROI)',
      value: { sort: 'roi', order: 'desc' }
    },
    {
      id: 'shop.filter.sort.profitability',
      title: 'По доходности',
      value: { sort: 'earnings', order: 'desc' }
    },
    {
      id: 'shop.filter.sort.powerEfficiency',
      title: 'По энергоэффективности',
      value: { sort: 'efficiency', order: 'desc' }
    }
  ]

  const AVAILABILITY_OPTIONS = [
    {
      title: formatMessage({
        id: 'shop.filter.availability.all',
        defaultMessage: 'Все устройства'
      }),
      value: null
    },
    {
      title: formatMessage({
        id: 'shop.filter.availability.inStock',
        defaultMessage: 'В наличии'
      }),
      value: false
    },
    {
      title: formatMessage({
        id: 'shop.filter.availability.preorder',
        defaultMessage: 'Предзаказ'
      }),
      value: true
    }
  ]

  const form = useForm<DeviceFilterStore>({
    defaultValues: {
      totalPrice,
      pricePerTH
    }
  })

  const { setValue, getValues } = form

  useEffect(() => {
    if (data) {
      const { maxPrice, maxPricePerTH, minPrice, minPricePerTH } =
        data?.pages[0]

      if (maxPrice === minPrice && maxPricePerTH === minPricePerTH) {
        setAreSamePrices(true)
      } else {
        const formattedMaxPrice = Math.ceil(maxPrice / step) * step
        const formattedMinPrice = Math.floor(minPrice / step) * step
        const formattedMaxPricePerTH =
          Math.ceil(maxPricePerTH / stepPerTH) * stepPerTH
        const formattedMinPricePerTH =
          Math.floor(minPricePerTH / stepPerTH) * stepPerTH

        setMaxPrice(formattedMaxPrice)
        setMinPrice(formattedMinPrice)

        setMaxPricePerTH(formattedMaxPricePerTH)
        setMinPricePerTH(formattedMinPricePerTH)

        if (!getValues('totalPrice.to') && !getValues('totalPrice.from')) {
          setValue('totalPrice', {
            from: formattedMinPrice,
            to: formattedMaxPrice
          })

          if (!getValues('pricePerTH.to') && !getValues('pricePerTH.from')) {
            setValue('pricePerTH', {
              from: formattedMinPricePerTH,
              to: formattedMaxPricePerTH
            })
          }
        }
      }
    }
  }, [data, step, stepPerTH, getValues, setValue])

  const onReorder = ({
    target: {
      value: { sort, order }
    }
  }: {
    target: { value: MinersSort }
  }) => {
    reorder({ sort, order })
  }

  const sortParams = {
    defaultValue: { sort, order },
    value: { sort, order },
    onChange: onReorder
  }

  return {
    data,
    form,
    isFetching,
    paymentFailed,
    isFetchingNextPage,
    sortParams,
    SORT_OPTIONS,
    AVAILABILITY_OPTIONS,
    fetchNextPage,
    maxPrice,
    maxPricePerTH,
    minPrice,
    minPricePerTH,
    areSamePrices
  }
}
